/**
 * siteGate — la garde de mise en service du site desktop (07/09/2026).
 *
 * Le site remplace le Livre du lobby comme page d'atterrissage des grands
 * écrans (memory/strategie/SITE_WEB_DESKTOP.md). Il se construit en vagues, et
 * une vague seule ne doit pas changer ce que la production sert : tant que
 * l'accueil et les fiches ne sont pas tous livrés, le site reste DERRIÈRE
 * cette garde. Steven peut le voir avant tout le monde, sur la production
 * même, avec `?site=1` (mémorisé dans le navigateur) — et le refermer avec
 * `?site=0`.
 *
 * Trois signaux, par ordre de force :
 *   1. `?site=1` / `?site=0` dans l'URL — et le choix est mémorisé ;
 *   2. le choix mémorisé (localStorage) ;
 *   3. la variable de build `REACT_APP_SITE_DESKTOP` (`1` = ouvert à tous).
 *
 * ✅ **LA GARDE EST LEVÉE DEPUIS LE 08/09/2026 AU SOIR** — une ligne, un
 * commit, une PR dédiée, comme ce fichier l'avait prévu au premier jour.
 *
 * ⚑ ET ELLE A ÉTÉ LEVÉE À MOITIÉ, EXPRÈS. Steven a choisi d'ouvrir en deux
 * temps : les visiteurs d'abord, Google plus tard. Le second interrupteur
 * (`SITE_DESKTOP`, côté serveur, qui publie le plan des 2 317 adresses) reste
 * ÉTEINT — parce que c'est le seul geste difficile à défaire : une adresse
 * annoncée puis retirée coûte plus cher qu'une adresse jamais annoncée. On
 * regarde le site vivre entre les deux.
 *
 * L'asymétrie est donc voulue et elle a un sens : **le plan ne doit jamais
 * s'ouvrir avant la façade** — un sitemap servi sur un front fermé envoie les
 * robots sur des pages que personne ne voit. L'inverse est sain : un site
 * ouvert sans plan est simplement discret.
 *
 * Ce qui reste vrai après la levée : `?site=0` referme toujours pour un
 * navigateur, et `?site=1` rouvre. Le choix du visiteur prime sur le défaut.
 */
export const SITE_STORE_KEY = 'medard.site';
export const SITE_PARAM = 'site';
/**
 * Le défaut de la maison : **REFERMÉ le 15/09/2026**, à la demande de Steven.
 *
 * > « Comme on est encore en face de development, je pense que le site doit
 * >   être avec un accès, on peut marquer coming soon […] par rapport à mes
 * >   parents et au manoir qui arrivent bientôt, c'est pas encore ouvert. »
 *
 * Il avait été ouvert le 08/09 au soir, les huit pages étant livrées. Mais le
 * MANOIR, lui, n'ouvre pas encore : une vitrine qui montre huit suites pour un
 * hôtel qui n'accueille personne promet ce que la maison ne peut pas tenir.
 * La façade se referme donc sur une page d'attente (`SiteBientot`), et deux
 * chemins la rouvrent : `?site=1` (le lien privé, déjà là) et le mot de passe
 * de la porte, vérifié au SERVEUR (`backend/site_acces.py`).
 *
 * ⚠ Ce qui n'a PAS bougé : `SITE_DESKTOP` côté serveur, qui publie le plan aux
 * moteurs, reste éteint — et c'est plus vrai que jamais. *Le plan ne s'ouvre
 * jamais avant la façade.*
 */
export const SITE_DEFAULT = false;

function readParam(search) {
    if (typeof search !== 'string' || !search) return null;
    try {
        const v = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(SITE_PARAM);
        if (v === '1' || v === 'true') return true;
        if (v === '0' || v === 'false') return false;
    } catch { /* URL illisible : pas de signal */ }
    return null;
}

function readStored(storage) {
    try {
        const v = storage?.getItem(SITE_STORE_KEY);
        if (v === '1') return true;
        if (v === '0') return false;
    } catch { /* stockage refusé : pas de signal */ }
    return null;
}

function remember(storage, value) {
    try {
        storage?.setItem(SITE_STORE_KEY, value ? '1' : '0');
    } catch { /* mémoire seule : la garde tient quand même pour cette page */ }
}

/**
 * Le site est-il ouvert pour ce navigateur ?
 * @param {{search?: string, storage?: Storage|null, env?: string|undefined}} signals
 */
export function siteEnabled({
    search = typeof window !== 'undefined' ? window.location.search : '',
    storage = typeof window !== 'undefined' ? safeStorage() : null,
    env = process.env.REACT_APP_SITE_DESKTOP,
} = {}) {
    const fromUrl = readParam(search);
    if (fromUrl !== null) {
        remember(storage, fromUrl);
        return fromUrl;
    }
    const stored = readStored(storage);
    if (stored !== null) return stored;
    if (env === '1') return true;
    if (env === '0') return false;
    return SITE_DEFAULT;
}

/**
 * Le mot de passe a été accepté par le serveur : ce navigateur entre.
 *
 * ⚑ La mémoire est la MÊME que celle de `?site=1` — une seule notion
 * d'« ouvert pour ce navigateur », pas deux. Deux mémoires pour un seul fait
 * finissent toujours par se contredire, et c'est alors l'invité qui arbitre.
 */
export function ouvrirLeSite(storage = typeof window !== 'undefined' ? safeStorage() : null) {
    remember(storage, true);
}

function safeStorage() {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}
